import './index.css'
import React, {useContext, useEffect, useRef, useState} from "react";
import SettingModal from "../home/component/SettingModal.tsx";
import PlayTipModal from "../home/component/PlayTipsModal.tsx";
import CountDownBar from "./component/CountDownBar/CountDownBar.tsx";
import GridLetterCard from "./component/GridLetterCard/GridLetterCard.tsx";
import Keyboard from "./component/Keyboard/Keyboard.tsx";
import DailyGameEndDialog from "./component/DailyGameEndDialog.tsx";
import {ThemeContext} from "../../providers/themeContext/theme-context.tsx";
import {PageEnum} from "../../types/IPage.ts";
import {useMusicContext} from "../../providers/musicProvider/music-player-context.tsx";
import {LocalStorageKey} from "../../types/IGameStat.ts";
import {GAEvents} from "../../common/utils/GA4/GAEvents.ts";
import useLoading from "../../providers/loadingContext/useLoading.ts";
import {apiService} from "../../common/utils/http/api-service.ts";
import {IDailyWordStatus, IDailyGameCorrectWord} from "../../common/utils/http/IResponse.ts";
import {isEmptyOrNullOrUndefined} from "../../common/utils/Datautil.ts";
import Countdown from "../../components/CountDown/CountDown.tsx";
import debounce from "lodash/debounce";

interface Props {
    currentPage?: PageEnum;
    switchPage: (page: PageEnum) => void;
    handleGameWinReward: (gameType: string) => void;
    handleShareReward: () => void;
}

// 每日游戏
const DailyGame: React.FC<Props> = ({switchPage, handleGameWinReward, handleShareReward}) => {
    // 主题全局管理器
    const {imgUrls, theme} = useContext(ThemeContext);
    // 加载中弹窗
    const {showLoading, hideLoading} = useLoading();
    // 全局音乐provider
    const musicManager = useMusicContext();
    const targetRef = useRef<HTMLDivElement>(null)
    // 提示的字母和位置
    const [hintLetter, setHintLetter] = useState<{ letter: string, state: number }>({letter: '', state: 0})
    // 是否已经提示过了
    const [hasHint, setHasHint] = useState<boolean>(false)
    // 是否显示每日挑战失败的弹窗提示
    const [showGameEndTip, setShowGameEndTip] = useState<boolean>(false)
    // 是否显示游戏提示
    const [showPlayTip, setShowPlayTip] = useState<boolean>(false)
    // 是否弹出设置弹窗
    const [showSetting, setShowSetting] = useState<boolean>(false)
    // 已经输入的单词列表
    const [inputWords, setInputWords] = useState<string[]>([])
    // 正确的单词
    const [correctWord, setCorrectWord] = useState<IDailyGameCorrectWord | null>(null)
    // 已经输入的单词列表里所有错误的字母，不可重复
    const [wrongLetters, setWrongLetters] = useState<string[]>([])
    // 倒计时的秒数时间
    const [countDownSeconds, setCountDownSeconds] = useState<number>(1800)
    // 游戏状态，0未开始，1开始，2结束【输了】，3结束【赢了】
    const [gameState, setGameState] = useState<number>(0)
    // 整体的游戏状态
    const [dailyGameStatus, setDailyGameStatus] = useState<IDailyWordStatus | null>(null)
    // 进入页面就开始加载正确单词
    useEffect(() => {
        loadDailyCorrectWord()
        loadDailyGameStatus();
    }, []);

    // 获取每日游戏的游戏状态
    const loadDailyGameStatus = debounce(async () => {
        try {
            showLoading()
            const response = await apiService.getDailyGamePlayStatus()
            if (response.code === 200) {
                setDailyGameStatus(response.data)
                // 设置提示词
                const res = response.data
                setInputWords(res.totalWords ?? [])
                setCountDownSeconds(1800 - (res.playTime ?? 0))
                setGameState(res.gameStatus ?? 1)
                setHintLetter(res.hintWord ?? {letter: '', state: 0})
                if (res.hintUsed) {
                    setHasHint(true)
                }
                const cw = response.data.correctWord
                // 判断键盘错误字母
                for (let i = 0; i < res?.totalWords.length; i++) {
                    const letters = res?.totalWords[i].toLowerCase().split('')

                    letters.forEach(letter => {
                        if (!cw.includes(letter)) {
                            setWrongLetters(prevWrongLetters => [...prevWrongLetters, letter.toLowerCase()]);
                        }
                    })
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            hideLoading()
        }
    }, 100)

    // 获取每日游戏正确单词
    const loadDailyCorrectWord = debounce(async () => {
        try {
            showLoading()
            const response = await apiService.getDailyGameWords()
            if (response.code === 200) {
                setCorrectWord(() => response.data)
            }
        } catch (error) {
            console.log(error)
            setCorrectWord(null)
        } finally {
            hideLoading()
        }
    }, 100)

    // 上传游戏数据
    const sendDailyGameStatus = async (status: number, totalWords: string[]) => {
        const params: IDailyWordStatus = {
            date: correctWord?.publishDate,
            playTime: status === 2 ? 1800 : 1800 - countDownSeconds,
            totalWords: totalWords,
            hintUsed: !isEmptyOrNullOrUndefined(hintLetter.letter),
            hintWord: hintLetter,
            correctWord: correctWord?.word ?? '',
            gameStatus: status,
        }
        const response = await apiService.submitDailyGameResult(params)
        if (response.code === 200) {
            setDailyGameStatus(response.data)
        }
    }

    useEffect(() => {
        // GA埋点
        GAEvents.dailyGameShow();
        // 首次进入每日游戏时，如果没有弹出过提示，就弹出提示
        showDailyTipDialog()
    }, []);

    // 从本地存储获取是否已经弹出过提示，如果弹出过，就不要再弹出，否则，就弹出来，然后保存到本地
    const showDailyTipDialog = () => {
        const dailyTipStatus = localStorage.getItem(LocalStorageKey.DAILY_TIP_STATUS);
        if (dailyTipStatus) {
            return;
        }
        // GA埋点
        GAEvents.howToPlayAutoShow();
        setShowPlayTip(true)
        // 保存标记到本地
        localStorage.setItem(LocalStorageKey.DAILY_TIP_STATUS, "hasShowed")
    }

    useEffect(() => {
        // 游戏失败
        if (gameState === 2) {
            // GA埋点
            GAEvents.dailyGameResult('Fail')
            // 延迟1秒显示
            setTimeout(() => {
                musicManager.playFailSound()
                setShowGameEndTip(true)
                // 游戏结束调用接口
                sendDailyGameStatus(2, inputWords);
            }, 3000);
        }

        // 如果游戏胜利，播放胜利音效
        if (gameState === 3) {
            // GA埋点
            GAEvents.dailyGameResult('Success')
            setTimeout(() => {
                musicManager.playSuccessSound()
                setShowGameEndTip(true)
            }, 3000);
        }
    }, [gameState]);

    // 设置游戏状态
    const handleGameState = (state: number) => {
        setGameState(state)
    }

    // 向单词列表添加单词
    const addWordToInputList = (word: string) => {
        const newWordList = [word, ...inputWords];
        setInputWords([...newWordList])
        // 每次添加完都判断一次错误单词
        checkWrongLetters(word, newWordList)
    }

    // 判定输入的单词是否正确，并更新错误单词列表
    const checkWrongLetters = (word: string, wordList: string[]) => {
        // 首先判断是否正确，如果正确，游戏结束
        if (correctWord?.word === word.toLowerCase()) {
            // 赢了
            handleGameState(3)
            // 游戏结束调用接口
            sendDailyGameStatus(3, wordList);
            // 胜利之后，查询奖励
            handleGameWinReward("daily")
            return
        } else {
            // 如果不正确，就扣除3分钟也就是180秒
            const seconds = countDownSeconds;
            if (seconds <= 180) {
                setCountDownSeconds(0)
                // 输了
                handleGameState(2)
            } else {
                setCountDownSeconds(seconds - 180)
            }
        }

        const letters = word.toLowerCase().split('')

        letters.forEach(letter => {
            if (!correctWord?.word?.includes(letter)) {
                setWrongLetters(prevWrongLetters => [...prevWrongLetters, letter.toLowerCase()]);
            }
        })
    }

    // 在设置中点击游戏提示
    const showPlayTipModal = () => {
        // 关闭设置弹窗
        setShowSetting(false)
        // 显示游戏提示
        setShowPlayTip(true)
    }

    // 返回按钮的点击事件
    const handleBackButton = async () => {
        // 有输入，并且是游戏中，才需要在退出的时候去保存，否则没有保存的必要
        if (inputWords.length > 0 && gameState === 1) {
            await sendDailyGameStatus(1, inputWords);
        }
        showHomeDownloadDialog();
        switchPage(PageEnum.HOME)
    }

    // 从本地存储获取是否已经弹出过下载提示，如果弹出过，就不要再弹出，否则，就弹出来，然后保存到本地
    const showHomeDownloadDialog = () => {
        const homeDownloadStatus = localStorage.getItem(LocalStorageKey.HOME_APP_DOWNLOAD_DIALOG);
        if (homeDownloadStatus) {
            return;
        }
        // 设置标记为未显示
        localStorage.setItem(LocalStorageKey.HOME_APP_DOWNLOAD_DIALOG, "noShowed")
    }

    // 跳转到挑战页面
    const toChallengePage = () => {
        // GA埋点
        GAEvents.dailyGameChallengeButtonClick(gameState === 2 ? 'Fail' : 'Success')
        switchPage(PageEnum.CHALLENGE)
    }

    // 胜利界面card-挑战模式的背景图样式
    const successCardStyle = (gameState === 3 || gameState === 2) ? {
        marginTop: '60px',
        backgroundImage: `url(${theme?.gameSettlementCardBg ?? imgUrls.dailyWinCardBgUrl})`,
        transition: 'background-image 1s ease-in-out'
    } : {};


    // 关闭每日游戏的结束弹窗，直接返回home
    const closeDailyGameModal = () => {
        // GA埋点
        GAEvents.dailyGameExitClick()
        setShowGameEndTip(false)
        // switchPage(PageEnum.HOME)
    }

    // 打开how to play提示弹窗
    const openHowToPlayModal = () => {
        // GA埋点
        GAEvents.dailyGameHelpClick()
        setShowPlayTip(true)
    }

    // 打开设置弹窗
    const openSettingModal = () => {
        // GA埋点
        GAEvents.dailyGameSettingClick()
        setShowSetting(true)
    }

    return (
        <div ref={targetRef} className="height-100-percent width-100-percent touch-action-none">
            <div className="global-page-background-img"
                 style={{backgroundImage: `url(${(gameState == 3 || gameState === 2) ? (theme?.gameBg ?? imgUrls.gameBgSuccessIconUrl) : (theme?.gameBg ?? imgUrls.gamePageBgIconUrl)})`}}/>
            {/*顶部标题栏*/}
            <div className="top-title-container"
                 style={{backgroundImage: `url(${theme?.gameTopBarBg ?? imgUrls.titleBarBgUrl})`}}>
                <img onClick={handleBackButton} className="height-30 width-30 margin-left-13 object-fit-contain"
                     src={theme?.gameTopBackBtn ?? imgUrls.goBackIconUrl} alt="pic"/>
                <div
                    className="font-size-20 color-white text-center position-fixed position-left-50-percent transform-rotate-minus-50 my-roboto-medium">Day {correctWord?.wordIssue}</div>
                <img onClick={openHowToPlayModal} className="height-30 width-30 object-fit-contain margin-left-auto"
                     src={theme?.gameQuestionBtn ?? imgUrls.gameTipIconUrl}
                     alt="pic"/>
                <img onClick={openSettingModal}
                     className="height-30 width-30 object-fit-contain margin-left-8 margin-right-10"
                     src={theme?.gameSettingBtn ?? imgUrls.settingIconUrl}
                     alt="pic"/>
            </div>
            {/* 倒计时进度框 */}
            {(gameState === 3 || gameState === 2) ? undefined :
                <CountDownBar gameState={gameState} handleGameState={handleGameState}
                              inputWords={inputWords}
                              countDownSeconds={countDownSeconds}
                              setCountDownSeconds={setCountDownSeconds}/>}
            {/*九宫格字母界面card*/}
            <div>
                <div
                    className={`margin-top-30  ${(gameState === 3 || gameState === 2) ? 'display-flex-column border-radius-11 img-no-repeat-center-contain height-340 width-340 margin-left-right-auto' : ''}`}
                    style={successCardStyle}
                >
                    {/*游戏九宫格界面*/}
                    <GridLetterCard wordList={inputWords} correctWord={correctWord?.word ?? ''}
                                    gameState={gameState}
                                    style={{top: `${(gameState === 3 || gameState === 2) ? '80px' : ''}`}}
                    />
                    {/*如果是daily游戏升级，在九宫格位置的底部显示文字信息*/}
                    {(gameState === 3 || gameState === 2) &&
                        <div
                            className="height-70 width-180 margin-left-right-auto display-flex-column display-flex-align-items-center display-flex-justify-content-center position-bottom-20 margin-bottom-20 margin-top-auto">
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-16 color-grey margin-bottom-10 my-roboto-regular">Answer
                            </div>
                            <div className="grid-5-columns grid-gap-3">
                                {correctWord?.word?.toUpperCase().split("").map((letter, index) => {
                                    return (
                                        <div key={letter + index} style={theme?.gameAnswerLetterCardBg ? {
                                            backgroundImage: `url(${theme?.gameAnswerLetterCardBg})`,
                                            backgroundSize: "cover",
                                            backgroundRepeat: "no-repeat"
                                        } : {}}
                                             className="display-flex-column display-flex-align-items-center display-flex-justify-content-center border-radius-4 width-33 height-40 bg-color-liner-white-green">
                                            <div className="font-size-27 color-white my-roboto-bold ">{letter}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>}
                </div>
                {/*键盘或者成功后的提示文字*/}
                {(gameState === 3 || gameState === 2) ?
                    <div style={{color: theme?.gameDescriptionTextColor}}
                         className="font-size-16 margin-top-12 margin-left-right-auto width-300 color-white line-height-30 text-center my-roboto-medium">
                        <div>You have completed today's game.</div>
                        <div>Would you like to guess other words?</div>
                        <div
                            className="display-flex-row display-flex-align-items-center display-flex-justify-content-center margin-top-14">
                            <img className="height-14 width-14" src={theme?.gameClockIcon ?? imgUrls.clockIconUrl}
                                 alt='clock'/>
                            <div style={{color: theme?.gameDescriptionTextColor}}
                                 className="font-size-16 color-white opacity-60-percent my-roboto-light margin-left-8">Next
                                Daily puzzle in
                            </div>
                        </div>
                        <Countdown className="font-size-32 my-roboto-bold text-italic margin-top-8"
                                   startCount={dailyGameStatus?.nextPlayTime ?? 0}
                                   interval={1000}
                                   showHours={true}
                                   onTick={() => {
                                   }}/>
                        <div onClick={toChallengePage}
                             style={{
                                 backgroundImage: `url(${theme?.gamePlayChallengeBtnBg})`,
                                 backgroundSize: 'cover',
                                 backgroundRepeat: "no-repeat"
                             }}
                             className={`${theme?.gamePlayChallengeBtnBg ? '' : 'play-challenge-btn-bg'} margin-top-20 border-radius-5  height-43 display-flex-row display-flex-justify-content-center display-flex-align-items-center`}>
                            <div style={{color: theme?.gamePlayChallengeBtnBg ? 'white' : ''}}
                                 className="font-size-16 my-roboto-medium color-blue">Play Challenge
                            </div>
                            <img className="width-18 height-18 margin-left-8"
                                 src={theme ? imgUrls.mouseWhiteIcon : imgUrls.mouseClickIconUrl}
                                 alt="icon"/>
                        </div>
                    </div>

                    :
                    <Keyboard
                        hasHint={hasHint}
                        setHasHint={(value) => setHasHint(value)}
                        hintLetter={hintLetter}
                        setHintLetter={setHintLetter}
                        correctWord={correctWord?.word ?? ''}
                        inputWords={inputWords}
                        gameState={gameState}
                        handleGameState={handleGameState}
                        addWordToInputList={addWordToInputList}
                        wrongLetters={wrongLetters}/>}
            </div>
            {/*daily游戏结束时的弹窗*/}
            <DailyGameEndDialog key={"dailyGameFailDialog"} isOpen={showGameEndTip} switchPage={switchPage}
                                onClose={closeDailyGameModal} correctWord={correctWord}
                                dailyGameStatus={dailyGameStatus} handleShareReward={handleShareReward}/>
            {/*设置的弹窗*/}
            <SettingModal key="homeSettingModal" isOpen={showSetting} onClose={() => setShowSetting(false)}
                          onShowPlayTip={showPlayTipModal}/>
            {/*游戏提示的弹窗*/}
            <PlayTipModal key="playTipModal" isOpen={showPlayTip} onClose={() => setShowPlayTip(false)}/>
        </div>
    );
};

export default DailyGame;
